import {Lang} from 'yougile-lang'
import {logger, UUID} from 'yougile-util'
import {
  AddCompanyReq,
  AddCompanyResp,
  DataObjectId,
  SandboxTree,
  User
} from 'yougile-model'
import {TrackingEvent} from '@common/tracking-events'
import {Backend} from '../../common/backend'
import {Tracking} from '../../common/tracking'
import {Refresh} from '../client/refresh'
import {SharedBoardsClient} from '../client/shared-boards-client'
import {Sync} from '../client/sync'
import {SyncEvents} from '../client/sync-events'
import {MessageBox} from '../components/message-box'
import {ChatUtils} from './chat-utils'
import {globals} from './globals-base'
import {PageService} from './page-service'

const chosenCompanyKey = 'chosenCompany'

export class Company {
  private static newCompanyListenerId: UUID

  public name: string
  public id: DataObjectId

  constructor(id: DataObjectId, name: string) {
    this.id = id
    this.name = name
  }

  public static get(companyId: DataObjectId): Company | undefined {
    const box = Sync.get({companyId})
    if (!box) {
      return undefined
    }
    return new Company(companyId, box.get(companyId).title)
  }

  public static setCurrent(
    companyId: DataObjectId,
    preventCloseChat = false
  ): void {
    globals.companyChanging = true
    globals.setCompanyId(companyId, true)
    setTimeout(() => {
      globals.companyChanging = false
      globals.setCompanyId(companyId)
    }, 100)

    const box = Sync.get({companyId})
    if (!preventCloseChat) {
      ChatUtils.closeChatIfOpened()
    }
    document.title = 'YouGile: ' + box.get(companyId).title
    Company.saveSelectedCompanyId(box.companyId)
    PageService.setPath(box.startPath() + '/')
  }

  public static saveSelectedCompanyId(companyId: DataObjectId): void {
    Backend.saveLocal(chosenCompanyKey, companyId)
  }

  public static getCurrent(): Company {
    return Company.find(globals.getCompanyId())
  }

  public static async setFirstCompany(companyId?: DataObjectId): Promise<void> {
    const profile = Sync.getProfile()
    if (!profile) {
      await Refresh.clearCredentials()
    }
    const user = profile.get(globals.currentUserId) as User
    let box: SandboxTree | undefined
    const valid = new Set(Company.listSandboxes().map(s => s.companyId))

    if (companyId !== undefined && valid.has(companyId)) {
      box = Sync.get({companyId})
    } else if (user.companyOrder && user.companyOrder.length > 0) {
      const first = user.companyOrder.filter(cid => valid.has(cid))[0]
      box = Sync.get({companyId: first || [...valid][0]})
    } else if (valid.size > 0) {
      box = Sync.get({companyId: [...valid][0]})
    }
    if (box) {
      document.title = 'Yougile: ' + box.get(box.companyId).title
      globals.setCompanyId(box.companyId)
    } else {
      globals.setCompanyId(undefined)
      void Tracking.send(TrackingEvent.companyCreateAuto)
      await Company.create('My company')
    }
  }

  public static async init(): Promise<void> {
    Sync.listSandboxes().forEach(sb => {
      globals.onCompanyChange(() => {
        Company.kickOutIfDeleted(sb)
      })
      SyncEvents.addListener(sb.companyId, sb.companyId, () => {
        Company.kickOutIfDeleted(sb)
      })
    })

    const path = PageService.getPath()
    const chosenCompanyId = Backend.loadLocal(chosenCompanyKey) as
      | DataObjectId
      | undefined
    const chosenCompany =
      chosenCompanyId &&
      Sync.listSandboxes().find(s => s.companyId === chosenCompanyId)
    const box = Sync.listSandboxes().find(s => path.startsWith(s.startPath()))
    if (
      !box ||
      box.get(box.companyId).deleted ||
      box.companyId === Sync.profileId
    ) {
      if (chosenCompany) {
        globals.setCompanyId(chosenCompanyId)
        document.title =
          'Yougile: ' + chosenCompany.get(chosenCompany.companyId).title
      } else {
        await Company.setFirstCompany()
      }
    } else {
      document.title = 'Yougile: ' + box.get(box.companyId).title
      globals.setCompanyId(box.companyId)
    }
  }

  public static kickOutIfDeleted(box: SandboxTree): void {
    const hub = box.get(box.companyId)
    if (!hub) {
      return
    }
    if (hub.deleted && globals.getCompanyId() === box.companyId) {
      logger.log('Kick out!')
      ChatUtils.closeChatIfOpened()
      void Company.setFirstCompany()
    }
  }

  public static listSandboxes(): SandboxTree[] {
    return Sync.listSandboxes().filter(
      (s: SandboxTree) =>
        s.companyId !== Sync.profileId &&
        (s.isUserInCompany(globals.currentUserId) ||
          SharedBoardsClient.getId()) &&
        s.get(s.companyId) &&
        !s.get(s.companyId).deleted
    )
  }

  public static list(): Company[] {
    return Company.listSandboxes().map(s => {
      const c = s.get(s.companyId)
      return {name: c.title, id: c.id}
    })
  }

  static orderedList(): Company[] {
    return this.list().sort(this.orderComparer)
  }

  public static orderComparer(first: Company, second: Company): number {
    const {companyOrder} = Sync.getCurrentUser()

    return companyOrder.indexOf(first.id) - companyOrder.indexOf(second.id)
  }

  public static find(id: DataObjectId): Company | undefined {
    return Company.list().find(c => c.id === id)
  }

  public static async create(text: string): Promise<void> {
    if (!text) {
      return
    }
    void Tracking.send(TrackingEvent.companyCreate)
    const req: AddCompanyReq = {...Backend.loadCred(), companyName: text}
    const timer = SyncEvents.addNewCompanyListener(companyId => {
      void Tracking.send(TrackingEvent.companyCreateSuccess)
      Company.setCurrent(companyId)
      PageService.goTo('projects')
      void Sync.saveCachedData()
    })
    try {
      await Backend.postJSON<AddCompanyResp>('/data/add-company', req)
      MessageBox.show(Lang.msg.companyAdded, 'success')
      await Sync.actualizeDataFromServer(undefined)
    } catch (e) {
      MessageBox.show('Failed to add company: ' + e, 'danger')
      void Tracking.send(TrackingEvent.companyCreateError)
    } finally {
      SyncEvents.removeNewCompanyListener(timer)
    }
  }
}
